import { Component, Inject, OnInit, ViewChild } from '@angular/core'
import { FormArray, FormBuilder, FormGroup } from '@angular/forms'
import { select, Store } from '@ngrx/store'
import * as lodash from 'lodash'

import {
  createAddSignersForm,
  getSignersFormData,
  setAddSignersFormData,
} from '../components/add-roles-form/add-roles-form.component'
import {
  createPlaceFieldsForm,
  PlaceFieldsFormComponent,
  setPlaceFieldsFormData,
} from '../components/place-fields-form/place-fields-form.component'
import {
  createTemplateDetailsForm,
  getTemplateDetailsFormData,
  setTemplateDetailsFormData,
} from '../components/template-details-form/template-details-form.component'
import { PreviewTemplateDrawerService } from '../preview-template/preview-template-drawer.service'
import {
  ContractTemplate,
  ContractTemplateView,
  selectContractTemplateViewById,
  TemplateTypes,
  UpdateContractTemplate,
} from '@tokeet-frontend/signature'
import { ConfirmDialogService, Destroyable, SaveForm, Toaster, untilDestroy } from '@tokeet-frontend/tv3-platform'
import { SignPrepareService } from '@tv3/services/sign-prepare.service'
import { FileService } from '@tv3/services/file.service'
import { RenameTemplateDialogService } from '@tv3/containers/documents/templates/dialogs/rename/rename-template-dialog.service'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { SendTemplateDrawerService } from '@tv3/containers/documents/templates/send-drawer/send-template/send-template-drawer.service'
import { selectAutomationGroupsByTemplateId } from '@tokeet-frontend/automations'

export enum TemplateDrawerTab {
  Signers,
  Fields,
  Details,
}

@Component({
  selector: 'app-edit-template-drawer',
  templateUrl: './edit-template-drawer.component.html',
  styleUrls: ['./edit-template-drawer.component.scss'],
})
export class EditTemplateDrawerComponent extends Destroyable implements OnInit {
  @ViewChild('placeFieldsFormCom') placeFieldsFormCom: PlaceFieldsFormComponent

  isRolesEditable = true

  addSignersForm = createAddSignersForm(this.fb)
  fieldsForm = createPlaceFieldsForm(this.fb)
  templateDetailsForm = createTemplateDetailsForm(this.fb)

  currentTab = TemplateDrawerTab.Details
  tabs = TemplateDrawerTab

  template: ContractTemplateView

  get templateType(): TemplateTypes {
    return this.template?.type
  }

  get roles() {
    return lodash.map(this.addSignersForm.getRawValue(), 'role')
  }

  get fields() {
    return this.fieldsForm.get('fields').value
  }

  get templateFileUrl() {
    return this.template?.pdf_file_url
  }

  constructor(
    private dialog: MatDialogRef<EditTemplateDrawerComponent>,
    private fb: FormBuilder,
    private store: Store<any>,
    private toaster: Toaster,
    private previewDrawer: PreviewTemplateDrawerService,
    private confirm: ConfirmDialogService,
    private fileService: FileService,
    private signPrepareService: SignPrepareService,
    private sendTemplateDrawer: SendTemplateDrawerService,
    private renameDialog: RenameTemplateDialogService,
    @Inject(MAT_DIALOG_DATA) private data: { templateId: string; tab?: TemplateDrawerTab; isRolesEditable?: boolean }
  ) {
    super()
  }

  ngOnInit(): void {
    this.currentTab = this.data.tab || TemplateDrawerTab.Details

    this.store
      .pipe(select(selectContractTemplateViewById(this.data.templateId)), untilDestroy(this))
      .subscribe((template) => {
        this.template = template
        setAddSignersFormData(this.addSignersForm, template, this.isRolesEditable)
        setPlaceFieldsFormData(this.fieldsForm, template)
        setTemplateDetailsFormData(this.templateDetailsForm, template)
      })

    this.isRolesEditable = !!this.data?.isRolesEditable
    if (!!this.data?.isRolesEditable) {
      this.store
        .pipe(select(selectAutomationGroupsByTemplateId(this.data.templateId), untilDestroy(this)))
        .subscribe((automations) => {
          this.isRolesEditable = !automations?.length
        })
    }
  }

  close() {
    this.dialog.close()
  }

  private save(payload: Partial<ContractTemplate>) {
    this.store.dispatch(
      UpdateContractTemplate({
        id: this.template.id,
        payload,
      })
    )
  }

  onPreview() {
    const { font, font_size } = this.fieldsForm.getRawValue()
    const fields = this.placeFieldsFormCom.getFieldsConfig()
    this.previewDrawer.open({
      type: this.template.type,
      fields,
      pdfFileUrl: this.template.pdf_file_url,
      defaultFont: font,
      defaultFontSize: font_size,
    })
  }

  @SaveForm()
  onSaveSigners(form: FormArray) {
    const { roles, ...others } = getSignersFormData(form)
    const oldRoles = this.template.roles
    const removedRoles = lodash.difference(oldRoles, roles)
    if (!!removedRoles.length) {
      this.confirm
        .confirm({
          title: 'Warning!',
          body: 'You have removed/renamed your signers, fields for these signers will be cleared.',
        })
        .subscribe(() => {
          const fields = lodash.filter(this.template.fields, (f) => lodash.includes(roles, f.role))
          this.save({ roles, ...others, fields })
        })
    } else {
      this.save({ roles, ...others })
    }
  }

  isReadyToUse() {
    return this.signPrepareService.isTemplateReady(this.template.type, this.template.roles, this.template.fields)
  }

  onDownload() {
    this.fileService.downloadToLocal(this.template.pdf_file_url, `${this.template.name}.pdf`).subscribe()
  }

  onSend() {
    this.sendTemplateDrawer.open(this.template.template_id)
  }

  onRename() {
    this.renameDialog.open(this.template)
  }

  @SaveForm()
  onSaveFields(form: FormGroup) {
    if (!this.placeFieldsFormCom.isTemplateReady()) {
      this.toaster.warning($localize`To process to next step, please add Signature field for each role.`)
      return
    }
    const { font, font_size } = form.getRawValue()
    const fields = this.placeFieldsFormCom.getFieldsConfig()
    this.save({ fields, font, font_size })
  }

  @SaveForm()
  onSaveDetails(form: FormGroup) {
    const payload = getTemplateDetailsFormData(form, this.template)
    this.save(payload)
  }
}
